import { IntlShape } from 'react-intl'
import { HospitalizationDailyPointDTO } from '../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import { markPeaks, PeakAnalysis } from '../ServicesStatistics/peaks'
import { dayPoint } from '../ServicesStatistics/periodDetail'
import { parseIsoDate } from '../ServicesStatistics/statisticsUtils'
import { chipTitle, periodTitle } from '../ServicesStatistics/TimelineChart'

/**
 * Evolución diaria de la internación. Funciones puras, sin React: se calculan en el front con los días que ya llegaron en
 * el resumen (la serie diaria); no hay consulta aparte.
 */

/** Lo que se mide en el gráfico: el mismo dato en otra vista (SelectButton «Ocupación | Ingresos | Egresos»). */
export type HospitalizationMeasure = 'occupancy' | 'admissions' | 'discharges'

export const HOSPITALIZATION_MEASURES: HospitalizationMeasure[] = ['occupancy', 'admissions', 'discharges']

/**
 * Camas disponibles mínimas para que la ocupación de un día se tome en serio (marcador pleno y chance de ser pico). Con
 * menos de 10 camas, un solo paciente mueve el porcentaje 10 puntos o más: filtrando un sector chico, ningún día llega y el
 * gráfico queda con marcadores huecos y sin picos, que es lo honesto. Es el mismo piso de 10 que usan los picos de las otras
 * pantallas (`PEAK_MIN_ATTENTIONS`). El 25 % de la mediana de camas que suma `markPeaks` no cambia nada con un sitio de unas
 * 200 camas: todos los días las tienen. A VALIDAR por el servicio.
 */
export const EVOLUTION_MIN_BEDS = 10

/** El valor de la medida en el día; null si no hay con qué calcularlo (una ocupación sin camas no es 0 %). */
export function pointValue(point: HospitalizationDailyPointDTO, measure: HospitalizationMeasure): number | null {
  if (measure === 'occupancy') return point.occupancyPct
  return measure === 'admissions' ? point.admissions : point.discharges
}

/**
 * El día se dibuja con marcador hueco: si todavía no está cerrado (hoy y los días futuros), o, con la ocupación, si tiene
 * pocas camas (el % puede no ser representativo).
 */
export function isLowVolume(point: HospitalizationDailyPointDTO, measure: HospitalizationMeasure): boolean {
  return !point.complete || (measure === 'occupancy' && point.bedsAvailable > 0 && point.bedsAvailable < EVOLUTION_MIN_BEDS)
}

/**
 * Picos de la ocupación con el criterio de `markPeaks` (media + 2 desvíos entre los días que cuentan, al menos 7 días que
 * cuentan, como mucho 3) y las camas disponibles como base. Los días sin cerrar no cuentan. Con «Ingresos» y «Egresos» no hay picos: el volumen sube y baja
 * con la semana (la cirugía programada no se opera el domingo) y eso no es un problema.
 */
export function occupancyPeaks(points: HospitalizationDailyPointDTO[]): PeakAnalysis {
  return markPeaks(
    points.map((point) => ({ value: point.complete ? point.occupancyPct : null, attentions: point.bedsAvailable })),
    EVOLUTION_MIN_BEDS,
  )
}

/**
 * La ocupación del período calculada sobre las sumas de los días (pacientes-día sobre días-cama), nunca promediando los
 * porcentajes de cada día. Es lo que dibuja la línea de promedio si el indicador no la trae.
 */
export function weightedOccupancy(points: HospitalizationDailyPointDTO[]): number | null {
  const closed = points.filter((point) => point.complete)
  const beds = closed.reduce((sum, point) => sum + point.bedsAvailable, 0)
  if (beds <= 0) return null
  return (closed.reduce((sum, point) => sum + point.patientDays, 0) / beds) * 100
}

/** Ingresos o egresos por día en el período: el total sobre los días cerrados del rango. */
export function averagePerDay(points: HospitalizationDailyPointDTO[], measure: 'admissions' | 'discharges'): number | null {
  const closed = points.filter((point) => point.complete)
  if (closed.length === 0) return null
  return closed.reduce((sum, point) => sum + point[measure], 0) / closed.length
}

/** Título completo del día: «Sábado 19/09/2026». */
export function dayTitleOf(intl: IntlShape, isoDay: string): string {
  return periodTitle(intl, dayPoint(parseIsoDate(isoDay), 0, null, null), 'DAY')
}

/** Nombre del día en un chip: «Sábado 19/09». */
export function dayChipOf(intl: IntlShape, isoDay: string): string {
  return chipTitle(intl, dayPoint(parseIsoDate(isoDay), 0, null, null), 'DAY')
}
